import type { ReactElement } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { useInstructionUsers } from '@/hooks/api/useInstructionUsers';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { Badge, badgeVariants } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { InventoryUserListDialog } from './InventoryUserListDialog';

/** Cap on Projects badges rendered inline before collapsing into a "+N more"
 * popover, so a single user with many workspace folders can't make one table
 * row taller than the modal (KIR-3991). */
const MAX_VISIBLE_FOLDERS = 5;

/**
 * Renders a user's workspace folders as compact badges. The first
 * {@link MAX_VISIBLE_FOLDERS} show inline; any remainder collapse behind a
 * "+N more" badge that opens a scrollable, selectable popover so long folder
 * paths stay readable without inflating the row. "Global" is shown when a user
 * reported no folder scope.
 */
function WorkspaceFolderBadges({
  folders,
}: {
  folders: string[];
}): ReactElement {
  if (folders.length === 0) {
    return <span className="text-muted-foreground text-sm">Global</span>;
  }

  const visible = folders.slice(0, MAX_VISIBLE_FOLDERS);
  const overflow = folders.slice(MAX_VISIBLE_FOLDERS);

  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((workspace, idx) => (
        <Badge
          key={idx}
          variant="outline"
          className="text-xs max-w-[16rem] truncate"
          title={workspace}
        >
          {workspace}
        </Badge>
      ))}
      {overflow.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                badgeVariants({ variant: 'outline' }),
                'text-xs cursor-pointer hover:bg-accent hover:text-accent-foreground'
              )}
              aria-label={`Show ${overflow.length} more ${
                overflow.length === 1 ? 'project' : 'projects'
              }`}
            >
              +{overflow.length} more
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="max-h-60 w-64 overflow-y-auto p-2"
          >
            <div className="flex flex-col gap-1">
              {overflow.map((workspace, idx) => (
                <span
                  key={idx}
                  className="text-xs break-all select-text text-foreground"
                >
                  {workspace}
                </span>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

interface InstructionUserListModalProps {
  organizationId: string;
  contentId: string | null;
  /** Category scope for the list (KIR-3356): `undefined`/omitted = no scope
   * (the legacy content-scoped table), `null` = the untyped row, string = that
   * category. The live DataTable always passes the clicked row's category. */
  fileCategory?: string | null;
  onClose: () => void;
}

export function InstructionUserListModal({
  organizationId,
  contentId,
  fileCategory,
  onClose,
}: InstructionUserListModalProps): ReactElement {
  const { data, isLoading, error } = useInstructionUsers(
    organizationId,
    contentId,
    fileCategory
  );

  const users = data?.users ?? [];
  const userCount = users.length;

  return (
    <InventoryUserListDialog
      open={contentId !== null}
      onClose={onClose}
      title={`Users with this Instruction (${isLoading ? '...' : userCount})`}
      description={
        <>
          Folders listed per user are workspace roots for specific projects;
          &quot;Global&quot; means no folder scope was reported.
        </>
      }
      columns={[
        { label: 'User' },
        { label: 'Projects' },
        { label: 'Last Seen' },
      ]}
      isLoading={isLoading}
      error={error}
      isEmpty={userCount === 0}
      emptyMessage="No users found"
    >
      {users.map(user => (
        <TableRow key={user.userId}>
          <TableCell>
            <div>
              <div className="font-medium">{user.userName || 'Unknown'}</div>
              {user.userEmail && (
                <div className="text-sm text-muted-foreground">
                  {user.userEmail}
                </div>
              )}
            </div>
          </TableCell>
          <TableCell>
            <WorkspaceFolderBadges folders={user.workspaceFolders} />
          </TableCell>
          <TableCell>
            <RelativeTimeWithTooltip date={user.lastSeenAt} />
          </TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}
